import React, { useEffect, useState } from "react";
import adminService from "../../api/services/adminService";
import Loading from "../../components/Loading";
import AdminPanelUserModal from "../../Modals/AdminPanelUserModal";
import toast from "react-hot-toast";

const AdminPanelUsers = () => {
  const [users, setUsers] = useState([]);
  const [backendPage, setBackendPage] = useState(1);
  const [total, setTotal] = useState(0);
  const backendLimit = 50;
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [trigger, setTrigger] = useState(false);

  // frontend pagination
  const [frontendPage, setFrontendPage] = useState(1);
  const frontendLimit = 15;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const result = await adminService.getAllAdminPanelUsers(backendPage);
        console.log(result);
        const allUsers = result?.users || [];
        setUsers(allUsers);
        setTotal(result?.total || allUsers.length);
        setFrontendPage(1);
      } catch (error) {
        console.error("Error fetching admin panel users:", error);
        toast.error(error?.message || "Failed to fetch admin panel users");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [backendPage, trigger]);

  const openModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setTrigger(!trigger);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = (user) => {
    setDeleteUser(user);
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    try {
      await adminService.deleteAdminPanelUser(deleteUser.id);
      toast.success("User deleted successfully!");
      setDeleteUser(null);
      setTrigger(!trigger);
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error?.message || "Failed to delete user");
    }
  };

  // frontend slice 15 users
  const paginatedUsers = users.slice(
    (frontendPage - 1) * frontendLimit,
    frontendPage * frontendLimit
  );

  const filteredUsers = paginatedUsers.filter((user) => {
    const fullName = `${user.firstName || ""} ${
      user.lastName || ""
    }`.toLowerCase();
    return (
      fullName.includes(searchQuery.toLowerCase()) ||
      (user.email &&
        user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.contactNo &&
        user.contactNo.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.roleName &&
        user.roleName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  const totalPages = Math.ceil(users.length / frontendLimit);
  const backendTotalPages = Math.ceil(total / backendLimit);

  const handleFrontendPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFrontendPage(newPage);
    }
  };

  const handleBackendPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= backendTotalPages) {
      setBackendPage(newPage);
    }
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case "Admin":
        return "bg-purple-100 text-purple-800";
      case "Author":
        return "bg-blue-100 text-blue-800";
      case "CSR":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-4 w-full">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              Admin Panel Users
            </h1>
            <button
              onClick={openModal}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold">
              Add User
            </button>
          </div>

          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by name, email, phone, or role..."
              className="border px-3 py-2 rounded w-full max-w-md"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto bg-white shadow rounded-lg">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2">SR. NO</th>
                  <th className="px-4 py-2">First Name</th>
                  <th className="px-4 py-2">Last Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Contact Number</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Created At</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, idx) => (
                    <tr key={user.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {(frontendPage - 1) * frontendLimit + idx + 1}
                      </td>
                      <td className="px-4 py-2 font-medium">
                        {user.firstName}
                      </td>
                      <td className="px-4 py-2">{user.lastName}</td>
                      <td className="px-4 py-2">{user.email}</td>
                      <td className="px-4 py-2">{user.contactNo || "-"}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getRoleColor(
                            user.roleName
                          )}`}>
                          {user.roleName || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {user.createdDate
                          ? new Date(user.createdDate).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          {user.roleName !== "Admin" && (
                            <button
                              onClick={() => handleDelete(user)}
                              className="text-red-600 hover:text-red-800">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="currentColor">
                                <path
                                  fillRule="evenodd"
                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-8 text-center text-gray-500">
                      No admin panel users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {users.length > 0 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600">
                Showing {(frontendPage - 1) * frontendLimit + 1} to{" "}
                {Math.min(frontendPage * frontendLimit, users.length)} of{" "}
                {users.length} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleFrontendPageChange(frontendPage - 1)}
                  disabled={frontendPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed">
                  Previous
                </button>
                <span className="px-3 py-1">
                  Page {frontendPage} of {totalPages}
                </span>
                <button
                  onClick={() => handleFrontendPageChange(frontendPage + 1)}
                  disabled={frontendPage === totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed">
                  Next
                </button>
              </div>
              {backendTotalPages > 1 && (
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-gray-600">Backend Pages:</span>
                  {[...Array(backendTotalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => handleBackendPageChange(idx + 1)}
                      className={`px-3 py-1 border rounded ${
                        backendPage === idx + 1
                          ? "bg-red-500 text-white"
                          : "bg-white"
                      }`}>
                      {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <AdminPanelUserModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editingUser={editingUser}
        trigger={trigger}
        setTrigger={setTrigger}
      />

      {/* Delete Confirmation Modal */}
      {deleteUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Confirm Delete
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <strong>
                {deleteUser.firstName} {deleteUser.lastName}
              </strong>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteUser(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelUsers;
